import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Menu, X, LogIn, User, LogOut, ChevronDown,
  Building2, Calculator, Briefcase, BookOpen, Package, MapPin,
  Receipt, Download, Gift, Compass, ArrowRight, Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// ---------- Mega-menu content ----------

const leistungenMega = {
  primary: [
    { to: "/leistungen#firmenansiedlung", icon: MapPin,     title: "Firmenansiedlungen",      desc: "Standortwahl, Bewilligungen, Domizil." },
    { to: "/leistungen#gruendung",        icon: Building2,  title: "Gesellschaftsgründung",   desc: "GmbH, AG, Holding & Einzelfirma." },
    { to: "/leistungen#vr",               icon: Briefcase,  title: "Verwaltungsmandate",      desc: "Unabhängiger VR mit CH-Wohnsitz." },
    { to: "/leistungen#buchhaltung",      icon: BookOpen,   title: "Finanzbuchhaltung",       desc: "Buchhaltung & Jahresabschluss nach OR." },
    { to: "/leistungen#steuern",          icon: Calculator, title: "Steuerberatung",          desc: "Optimierung Schweiz ↔ Deutschland." },
    { to: "/vorratsgesellschaften",       icon: Package,    title: "Vorratsgesellschaften",   desc: "Schneller Start mit Bestandsfirma." },
  ],
  feature: {
    to: "/so-funktionierts",
    title: "So funktioniert's",
    desc: "Lernen Sie unseren 5-Schritte-Ablauf kennen und sehen Sie das Komplexitätsmodell für Gründung & Steuern.",
    cta: "Mehr erfahren",
    icon: Compass,
  },
};

const toolsMega = {
  primary: [
    { to: "/rechner",     icon: Calculator, title: "Kostenrechner",        desc: "In 4 Schritten zum unverbindlichen Angebot." },
    { to: "/preisliste",  icon: Receipt,    title: "Preisliste 2025",      desc: "Alle Pauschalen und Tarife im Überblick." },
    { to: "/downloads",   icon: Download,   title: "Downloads & Merkblätter", desc: "Vorlagen, Checklisten, Informations-PDFs." },
    { to: "/empfehlen",   icon: Gift,       title: "Empfehlungsprogramm",  desc: "CHF 500 Gutschrift pro erfolgreicher Empfehlung." },
  ],
  feature: {
    to: "/rechner",
    title: "Jetzt Kosten kalkulieren",
    desc: "Bekommen Sie in unter zwei Minuten eine erste Einschätzung der zu erwartenden Investitionen.",
    cta: "Zum Kostenrechner",
    icon: Sparkles,
  },
};

// ---------- Header ----------

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(null); // "leistungen" | "tools" | null
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => { setMobileOpen(false); setMegaOpen(null); }, [location.pathname, location.hash]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mega-menu when clicking outside the header
  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest("[data-mega-scope]")) setMegaOpen(null);
    };
    if (megaOpen) window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [megaOpen]);

  const openMega = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMegaOpen(key);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMegaOpen(null), 180);
  };

  return (
    <header
      data-mega-scope
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm"
          : "bg-white/80 backdrop-blur-md border-b border-transparent"
      }`}
      data-testid="site-header"
      onMouseLeave={scheduleClose}
    >
      <div className="mbw-container flex items-center justify-between h-24">
        <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="logo-link">
          <img src="/assets/mbw-logo-icon.png" alt="MBW Swiss AG Logo" className="w-14 h-14 object-contain" />
          <div className="leading-tight whitespace-nowrap">
            <div className="font-heading font-bold text-[#991B1B] text-xl tracking-tight">MBW Swiss AG</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Treuhand · Steuern</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="nav-desktop">
          <SimpleLink to="/" exact>Start</SimpleLink>

          <MegaTrigger
            label="Leistungen"
            active={location.pathname.startsWith("/leistungen") || location.pathname.startsWith("/vorratsgesellschaften") || location.pathname === "/so-funktionierts"}
            open={megaOpen === "leistungen"}
            onOpen={() => openMega("leistungen")}
            onScheduleClose={scheduleClose}
            testId="nav-mega-leistungen"
          />

          <MegaTrigger
            label="Tools & Preise"
            active={["/rechner", "/preisliste", "/downloads", "/empfehlen"].includes(location.pathname)}
            open={megaOpen === "tools"}
            onOpen={() => openMega("tools")}
            onScheduleClose={scheduleClose}
            testId="nav-mega-tools"
          />

          <SimpleLink to="/ueber-uns">Über uns</SimpleLink>
          <SimpleLink to="/kontakt">Kontakt</SimpleLink>
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user && user.id ? (
            <>
              <Link to="/portal" className="btn-secondary !py-2 !px-4 !text-sm" data-testid="header-portal-link">
                <User className="w-4 h-4" /> Portal
              </Link>
              <button onClick={logout} className="text-slate-500 hover:text-[#1A2433] p-2" data-testid="header-logout-btn" title="Abmelden">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-[#1A2433] px-3 py-2" data-testid="header-login-link">
                <LogIn className="w-4 h-4 inline mr-1.5" /> Login
              </Link>
              <Link to="/kontakt" className="btn-primary !py-2 !px-4 !text-sm" data-testid="header-contact-cta">
                Beratung anfragen
              </Link>
            </>
          )}
        </div>

        <button
          className="lg:hidden p-2 text-[#1A2433]"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mega-menu panel */}
      {megaOpen && (
        <div
          className="hidden lg:block absolute left-0 right-0 top-full bg-white border-t border-slate-200 shadow-xl"
          onMouseEnter={() => openMega(megaOpen)}
          onMouseLeave={scheduleClose}
          data-testid={`mega-panel-${megaOpen}`}
        >
          <div className="mbw-container py-8">
            {megaOpen === "leistungen" && <MegaPanel data={leistungenMega} />}
            {megaOpen === "tools"      && <MegaPanel data={toolsMega} />}
          </div>
        </div>
      )}

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="mbw-container py-4 space-y-1">
            <MobileLink to="/" exact>Start</MobileLink>

            <MobileAccordion title="Leistungen" items={[
              ...leistungenMega.primary.map((p) => ({ to: p.to, label: p.title })),
              { to: leistungenMega.feature.to, label: leistungenMega.feature.title, highlight: true },
            ]} />

            <MobileAccordion title="Tools & Preise" items={[
              ...toolsMega.primary.map((p) => ({ to: p.to, label: p.title })),
            ]} />

            <MobileLink to="/ueber-uns">Über uns</MobileLink>
            <MobileLink to="/kontakt">Kontakt</MobileLink>

            <div className="border-t border-slate-200 mt-2 pt-3 flex flex-col gap-2">
              {user && user.id ? (
                <>
                  <Link to="/portal" className="btn-secondary justify-center">Mein Portal</Link>
                  <button onClick={logout} className="btn-secondary justify-center">Abmelden</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn-secondary justify-center">Login</Link>
                  <Link to="/kontakt" className="btn-primary justify-center">Beratung anfragen</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

// ---------- Sub-components ----------

function SimpleLink({ to, children, exact }) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
          isActive
            ? "text-[#1A2433] bg-slate-100"
            : "text-slate-600 hover:text-[#1A2433] hover:bg-slate-50"
        }`
      }
      data-testid={`nav-link-${to.replace("/", "") || "home"}`}
    >
      {children}
    </NavLink>
  );
}

function MegaTrigger({ label, active, open, onOpen, onScheduleClose, testId }) {
  return (
    <button
      type="button"
      onMouseEnter={onOpen}
      onFocus={onOpen}
      onClick={() => (open ? onScheduleClose() : onOpen())}
      className={`px-3 py-2 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1 ${
        active || open
          ? "text-[#1A2433] bg-slate-100"
          : "text-slate-600 hover:text-[#1A2433] hover:bg-slate-50"
      }`}
      aria-expanded={open}
      data-testid={testId}
    >
      {label}
      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
  );
}

function MegaPanel({ data }) {
  const Feature = data.feature.icon;
  return (
    <div className="grid lg:grid-cols-12 gap-8">
      {/* Items grid */}
      <div className="lg:col-span-8 grid sm:grid-cols-2 gap-2">
        {data.primary.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              className="group flex gap-4 p-4 rounded-xl hover:bg-slate-50 transition border border-transparent hover:border-slate-200"
              data-testid={`mega-link-${item.to.replace(/[#/]/g, "-")}`}
            >
              <div className="w-10 h-10 rounded-lg bg-[#1A2433] flex items-center justify-center flex-shrink-0 group-hover:bg-[#991B1B] transition">
                <Icon className="w-5 h-5 text-[#DC2626] group-hover:text-white transition" />
              </div>
              <div className="min-w-0">
                <div className="font-heading font-bold text-[#1A2433] text-sm mb-0.5">{item.title}</div>
                <div className="text-xs text-slate-500 leading-snug">{item.desc}</div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Feature card */}
      <div className="lg:col-span-4">
        <Link
          to={data.feature.to}
          className="group block h-full p-6 rounded-2xl bg-gradient-to-br from-[#1A2433] to-[#0f1620] text-white relative overflow-hidden"
          data-testid="mega-feature"
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#991B1B] rounded-full blur-3xl"></div>
          </div>
          <div className="relative">
            <div className="w-10 h-10 rounded-lg bg-[#991B1B] flex items-center justify-center mb-4">
              <Feature className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-heading font-bold text-lg mb-2">{data.feature.title}</h3>
            <p className="text-sm text-slate-300 leading-relaxed mb-5">{data.feature.desc}</p>
            <span className="inline-flex items-center gap-1.5 text-sm text-[#DC2626] font-semibold">
              {data.feature.cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </Link>
      </div>
    </div>
  );
}

function MobileLink({ to, children, exact }) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        `block px-3 py-3 text-base font-medium rounded-md ${
          isActive ? "bg-slate-100 text-[#1A2433]" : "text-slate-700"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function MobileAccordion({ title, items }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-3 text-base font-medium text-slate-700"
      >
        {title}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pl-4 pb-2 flex flex-col gap-1">
          {items.map((it) => (
            <Link key={it.to} to={it.to}
              className={`px-3 py-2 text-sm rounded-md ${it.highlight ? "text-[#991B1B] font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>
              {it.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
